const BACK_NAVIGATION_STATE_KEY = "__backNavigation";
const PAGE_RESTORE_STATE_KEY = "__pageRestore";
const PAGE_RESTORE_STORAGE_KEY = "pageRestoreSnapshots";
const MAX_SNAPSHOTS = 100;
const SNAPSHOT_TTL = 1000 * 60 * 60 * 24;

const getStorage = () => {
    try {
        return window.sessionStorage;
    } catch (error) {
        return null;
    }
};

const readSnapshots = () => {
    const storage = getStorage();

    if (!storage) {
        return {};
    }

    try {
        const rawSnapshots = storage.getItem(PAGE_RESTORE_STORAGE_KEY);

        return rawSnapshots ? JSON.parse(rawSnapshots) : {};
    } catch (error) {
        return {};
    }
};

const writeSnapshots = (snapshots) => {
    const storage = getStorage();

    if (!storage) {
        return;
    }

    try {
        storage.setItem(PAGE_RESTORE_STORAGE_KEY, JSON.stringify(snapshots));
    } catch (error) {
        // noop
    }
};

const getEntrySnapshotKey = (pageKey, locationKey = "default") => `entry:${pageKey}:${locationKey}`;
const getPageSnapshotKey = (pageKey) => `page:${pageKey}`;

const pruneSnapshots = (snapshots) => {
    const now = Date.now();
    const entries = Object.entries(snapshots)
        .filter(([, snapshot]) => snapshot?.updatedAt && now - snapshot.updatedAt < SNAPSHOT_TTL)
        .sort(([, a], [, b]) => b.updatedAt - a.updatedAt)
        .slice(0, MAX_SNAPSHOTS);

    return Object.fromEntries(entries);
};

export const stripNavigationState = (state) => {
    if (!state || typeof state !== "object" || Array.isArray(state)) {
        return undefined;
    }

    const nextState = { ...state };

    delete nextState[BACK_NAVIGATION_STATE_KEY];
    delete nextState[PAGE_RESTORE_STATE_KEY];

    return Object.keys(nextState).length ? nextState : undefined;
};

export const savePageSnapshot = ({ pageKey, locationKey, snapshot }) => {
    if (!pageKey) {
        return;
    }

    const nextSnapshot = {
        ...snapshot,
        updatedAt: Date.now(),
    };
    const snapshots = pruneSnapshots({
        ...readSnapshots(),
        [getEntrySnapshotKey(pageKey, locationKey)]: nextSnapshot,
        [getPageSnapshotKey(pageKey)]: nextSnapshot,
    });

    writeSnapshots(snapshots);
};

const takeSnapshot = (snapshotKey) => {
    const snapshots = readSnapshots();
    const snapshot = snapshots[snapshotKey];

    if (!snapshot) {
        return null;
    }

    delete snapshots[snapshotKey];
    writeSnapshots(pruneSnapshots(snapshots));

    return snapshot;
};

export const takeEntrySnapshot = ({ pageKey, locationKey }) => {
    if (!pageKey || !locationKey) {
        return null;
    }

    return takeSnapshot(getEntrySnapshotKey(pageKey, locationKey));
};

export const takePageSnapshot = ({ pageKey }) => {
    if (!pageKey) {
        return null;
    }

    return takeSnapshot(getPageSnapshotKey(pageKey));
};

export const getBackNavigationDescriptor = (locationState) => locationState?.[BACK_NAVIGATION_STATE_KEY] || null;
export const getPageRestoreDescriptor = (locationState) => locationState?.[PAGE_RESTORE_STATE_KEY] || null;

export const buildBackNavigationTarget = (to, location, restoreKey) => {
    const target = typeof to === "string" ? { pathname: to } : { ...to };
    const targetState = stripNavigationState(target.state);
    const nextTarget = {
        ...target,
        state: {
            ...targetState,
            [BACK_NAVIGATION_STATE_KEY]: {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
                state: stripNavigationState(location.state),
                restoreKey,
            },
        },
    };

    return nextTarget;
};

export const buildBackNavigationFallback = (descriptor) => {
    if (!descriptor?.pathname) {
        return null;
    }

    return {
        pathname: descriptor.pathname,
        search: descriptor.search || "",
        hash: descriptor.hash || "",
        state: {
            ...(descriptor.state || {}),
            [PAGE_RESTORE_STATE_KEY]: {
                restoreKey: descriptor.restoreKey,
            },
        },
    };
};
